import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createCustomer, sendPasswordReset, selectAllCustomers, fetchCustomers, editCustomer, deleteCustomer } from '../../redux/adminSlice';
import CustomerModulesModal from '../CustomerModulesModal';
import CustomerDataModal from '../CustomerDataModal';
import CustomerDetailDrawer from '../CustomerDetailDrawer';
import { displayName, customerStatus, formatDate, formatStorageSize } from '../customerFormat';

export const useCustomerUtils = () => {
  const dispatch = useDispatch();
  const customers = useSelector(selectAllCustomers);

  // Returns the unwrapped thunk promise so the form can wait for the server
  // and keep its values when creation fails (the thunk only alerts on success).
  const handleAddCustomer = async (newCustomer) => {
     return dispatch(createCustomer(newCustomer)).unwrap();
  };

  // Returns the unwrapped thunk promise so the detail drawer can await the
  // save and show the server's own success/failure message inline instead
  // of relying on the shared state.error banner.
  const handleEditCustomer = async (customerId, updatedData) => {
     return dispatch(editCustomer({ customerId, updatedData })).unwrap();
  };

  // Returns the unwrapped thunk promise so the detail drawer can await the
  // delete and show the server's own failure message inline before deciding
  // whether to close itself.
  const handleDeleteCustomer = async (customerId) => {
     return dispatch(deleteCustomer(customerId)).unwrap();
  };

  // Returns the unwrapped thunk promise so the detail drawer can await the
  // send and show a server failure inline (dispatching without awaiting
  // used to drop a rejection silently - console.error only, no UI).
  const handleSendPasswordReset = async (email) => {
     return dispatch(sendPasswordReset(email)).unwrap();
  };

  const fetchCustomersData = () => {
    dispatch(fetchCustomers());
  };

  return {
    customers,
    handleAddCustomer,
    handleEditCustomer,
    handleDeleteCustomer,
    handleSendPasswordReset,
    fetchCustomersData,
  };
};

export const CustomerTable = ({ customers, onSendPasswordReset, onEditCustomer, onDeleteCustomer }) => {
  const [search, setSearch] = useState('');
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [modulesCustomer, setModulesCustomer] = useState(null);
  const [dataCustomer, setDataCustomer] = useState(null);

  const customersArray = useMemo(
    () => (Array.isArray(customers) ? customers : customers?.customers || []),
    [customers]
  );

  // Case-insensitive substring match over the fields an admin would actually
  // search by, then sorted by the same label the table displays - so the
  // visible order always matches what the admin typed to find.
  const filteredCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = query
      ? customersArray.filter((customer) => {
          const haystacks = [
            displayName(customer),
            customer.ownerName,
            customer.email,
            customer.companyName,
            customer.name,
          ];
          return haystacks.some((value) => (value || '').toLowerCase().includes(query));
        })
      : customersArray;
    return [...matches].sort((a, b) => displayName(a).localeCompare(displayName(b)));
  }, [customersArray, search]);

  // Store only the id, not the customer object, and re-derive it from the
  // live array on every render - so if redux patches this customer (e.g.
  // this drawer's own save, or another admin's setCustomerModules), the
  // open drawer reflects it immediately instead of the stale snapshot from
  // when it was opened.
  const openCustomer = openCustomerId
    ? customersArray.find((customer) => customer.id === openCustomerId) || null
    : null;

  const handleModulesClick = (customer) => {
    setModulesCustomer(customer);
  };

  const handleDataClick = (customer) => {
    setDataCustomer(customer);
  };

  if (customersArray.length === 0) {
    return <p className="text-[#5b6d7a]">No customers found. Add a customer to get started.</p>;
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search company, owner or email"
          className="w-full max-w-sm rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
        />
        {search.trim() && (
          <span className="text-xs font-semibold text-[#64798a]">
            {filteredCustomers.length} of {customersArray.length}
          </span>
        )}
      </div>

      {filteredCustomers.length === 0 ? (
        <p className="text-[#5b6d7a]">No customer matches &ldquo;{search.trim()}&rdquo;.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-[#1d2b33]">
            <thead>
              <tr>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Company
                </th>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Owner
                </th>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Email
                </th>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Status
                </th>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Workers
                </th>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Jobsites
                </th>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Storage
                </th>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Created
                </th>
                <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => {
                const status = customerStatus(customer);
                const name = displayName(customer);
                const showRawName = customer.companyName && customer.name && customer.companyName !== customer.name;

                return (
                  <tr
                    key={customer.id}
                    onClick={() => setOpenCustomerId(customer.id)}
                    className="cursor-pointer border-b border-[rgba(150,165,180,.15)] hover:bg-[#f4f7fa]"
                  >
                    <td className="p-2">
                      <div>{name}</div>
                      {showRawName && <div className="text-xs text-[#64798a]">{customer.name}</div>}
                    </td>
                    <td className="p-2">
                      {customer.ownerName || <span className="text-[#93a4b3]">—</span>}
                    </td>
                    <td className="p-2">{customer.email}</td>
                    <td className="p-2">
                      <span className="inline-flex items-center gap-1.5" title={status.hint}>
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: status.color }} />
                        {status.label}
                      </span>
                    </td>
                    <td className="p-2 text-[#1d2b33]">
                      {customer.usageStats ? customer.usageStats.employees : <span title="updated nightly">—</span>}
                    </td>
                    <td className="p-2 text-[#1d2b33]">
                      {customer.usageStats ? customer.usageStats.jobsites : <span title="updated nightly">—</span>}
                    </td>
                    <td className="p-2 text-[#1d2b33]">
                      {customer.usageStats ? (
                        <span title={`${customer.usageStats.storageObjects ?? 0} file(s)`}>
                          {formatStorageSize(customer.usageStats.storageBytes || 0)}
                        </span>
                      ) : (
                        <span title="updated nightly">—</span>
                      )}
                    </td>
                    <td className="p-2 text-[#1d2b33]">{formatDate(customer.createdAt)}</td>
                    <td className="p-2">
                      <button
                        type="button"
                        onClick={() => setOpenCustomerId(customer.id)}
                        className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-sm font-semibold text-[#1d2b33] transition hover:bg-[#f4f7fa]"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openCustomer && (
        <CustomerDetailDrawer
          customer={openCustomer}
          onClose={() => setOpenCustomerId(null)}
          onSave={onEditCustomer}
          onSendPasswordReset={onSendPasswordReset}
          onDelete={onDeleteCustomer}
          onOpenModules={handleModulesClick}
          onOpenData={handleDataClick}
          // CustomerModulesModal/CustomerDataModal render at z-50, above this
          // drawer's z-40, and stay mounted here while open - so the
          // drawer's own Escape handler must not close ITSELF (leaving the
          // modal floating over the bare table) while either is open.
          blocked={Boolean(modulesCustomer || dataCustomer)}
        />
      )}
      {modulesCustomer && (
        <CustomerModulesModal
          customer={modulesCustomer}
          onClose={() => setModulesCustomer(null)}
        />
      )}
      {dataCustomer && (
        <CustomerDataModal
          customer={dataCustomer}
          onClose={() => setDataCustomer(null)}
        />
      )}
    </>
  );
};
